/**
 * LEGACY rail: Delphi (`/tools/delphi/**`) is its only consumer. Delphi opts
 * out of the V2 shell frame (shell-chrome resolver), so MiniAppLayout still
 * mounts this rail, unframed, with its minimal variant, hover-expand and the
 * mobile hamburger. Every other route is framed by the V2 shell's NavRail.
 * Do not trim behaviour here for the V2 shell; it is Delphi's "as is" chrome.
 * The `library` nav key keeps matching Delphi's manifest `hideNavKeys`.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LayoutGrid, Users, UsersRound, TrendingUp, History, Settings, Grid3X3, LogOut, Shield, ArrowLeftRight, BookOpen, Waves, ListTodo, Radio, House, Menu, Moon, Check, Eye, X } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useClerk } from '@clerk/clerk-react';
import { useQueryStates } from 'nuqs';
import { useUserControlsContext } from '../contexts/UserControlsContext';
import { getInitials, emailToDisplayName } from '../view-system/utils/displayFormatters';
import { useImpersonation } from '../hooks/useImpersonation';
import { openImpersonationPalette } from '../lib/impersonation-palette-store';
import { modalParams } from '../lib/urlParams';
import { useMediaQuery } from './flow/useMediaQuery';
import { useRoleView, useSetViewAsRole } from '../hooks/useRoleView';
import { TeamLensItems } from './TeamLensItems';
import { teamLabel } from '@shared/flow/role-view';
import type { FlowRole } from '@shared/flow/role';
import { setTheme, useThemeSync } from '../lib/theme';
import type { MiniAppShellRailConfig } from '../../../../mini-apps/_shared/types';

// Below this width, the hover-driven desktop rail is replaced by a floating
// hamburger trigger — see Key Technical Decision 10 in the round-2 plan.
// Value picked to sit in the same 720-768px neighborhood as
// ResponsivePopover's `(max-width: 719px)` breakpoint.
const MOBILE_BREAKPOINT_QUERY = '(max-width: 767px)';

const DEFAULT_COLLAPSED_WIDTH = 56;
const DEFAULT_EXPANDED_WIDTH = 180;
const MINIMAL_COLLAPSED_WIDTH = 40;
const MINIMAL_ICON_CLASS = 'w-4 h-4';
const DEFAULT_ICON_CLASS = 'w-5 h-5';
const MINIMAL_RAIL_PL = 28; // more breathing room from the screen edge
const MINIMAL_RAIL_PR = 4; // pr-1
const MINIMAL_PILL_INSET = 6; // thin inner padding between border and item boxes
const MINIMAL_ITEM_PX = 8; // px-2 — item box horizontal padding (hugs text)
const MINIMAL_ICON_SLOT = 24; // w-6 — fixed icon column; icons stay put while boxes grow right
const MINIMAL_PILL_COLLAPSED = MINIMAL_PILL_INSET * 2 + MINIMAL_ITEM_PX * 2 + MINIMAL_ICON_SLOT;
const MINIMAL_EXPAND_MS = 'duration-300';
const MINIMAL_EXPAND_EASE = 'ease-[cubic-bezier(0.4,0,0.2,1)]';

function minimalPillMaxWidth(expandedCapPx: number): string {
  return `min(${expandedCapPx}px, calc(100vw - 3rem))`;
}

function minimalNavMaxWidth(expandedCapPx: number): string {
  return `min(${expandedCapPx + MINIMAL_RAIL_PL + MINIMAL_RAIL_PR}px, calc(100vw - 1.5rem))`;
}

import { canAccessSurface, type AppViewMode } from './flow/shell-next/rail/rail-items';

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  isRoute?: boolean;
  route?: string;
  external?: boolean;
}

const MAIN_NAV_ITEMS: NavItem[] = [
  { key: 'home', label: 'Home', icon: <House className="w-5 h-5" />, isRoute: true, route: '/home' },
  { key: 'accounts', label: 'Accounts', icon: <Users className="w-5 h-5" /> },
  { key: 'pipeline', label: 'Pipeline', icon: <TrendingUp className="w-5 h-5" /> },
  { key: 'categories', label: 'Categories', icon: <LayoutGrid className="w-5 h-5" /> },
  { key: 'library', label: 'Toolkit', icon: <BookOpen className="w-5 h-5" />, isRoute: true, route: '/library' },
  { key: 'open-loops', label: 'Open Loops', icon: <ListTodo className="w-5 h-5" />, isRoute: true, route: '/open-loops' },
  { key: 'signals', label: 'Signals', icon: <Radio className="w-5 h-5" />, isRoute: true, route: '/signals' },
  // { key: 'agenda', label: 'Agenda', icon: <Calendar className="w-5 h-5" />, isRoute: true, route: '/agenda' }, // TODO: re-enable after agenda page is fixed
];

interface RolePillProps {
  team: FlowRole;
  /** The rep's real team; null for MEMBER/REVOPS, who have no team to "return" to. */
  realTeam: FlowRole | null;
  /** True while a lens is active (effective team differs from the real one). */
  viewingAs: boolean;
  /** Collapsed desktop rail (56px): team label only, no "User Role" prefix
   * and no x — the full form needs more room than the collapsed rail has
   * (Deferred to Implementation: collapsed-rail pill form). */
  collapsed: boolean;
  /** Mobile: a fixed chip beside the hamburger trigger instead of an inline
   * block in the avatar section. */
  fixed?: boolean;
  onOpenMenu: () => void;
  onClear: () => void;
}

/**
 * Team badge + lens reminder pill (R6) — shown whenever the rep has an
 * effective team. Without a lens it is just the team label ("GS"); while
 * the lens differs from the real team it reads "User Role: <Team>" with an
 * x that clears the lens (`setTeam(null)`). Neutral `nightshade` chip;
 * never `tangerine` (that color is reserved for the impersonation ring on
 * the avatar, Decision 9 / plan note) and the avatar itself gets no ring
 * here. Clicking the label opens the avatar/hamburger menu so the rep can
 * change teams.
 */
function RolePill({ team, realTeam, viewingAs, collapsed, fixed, onOpenMenu, onClear }: RolePillProps) {
  const label = teamLabel(team);
  // Copy per state (L006): no lens -> a plain team badge; the collapsed pill
  // renders no x, so its title must not mention one; a rep with no real
  // team has no "your team" to return to, so the x stops the lens instead.
  const clearLabel = realTeam ? 'Return to my team' : `Leave User Role: ${label}`;
  const clearHint = collapsed
    ? 'Click to change teams.'
    : realTeam
      ? 'Click x to return to your team.'
      : `Click x to leave User Role: ${label}.`;
  const title = viewingAs
    ? `Terra is showing the ${label} team's views. Your permissions and attribution are unchanged. ${clearHint}`
    : `You are on the ${label} team`;
  const showFull = viewingAs && !collapsed;
  const testidBase = fixed ? 'nav-rail-mobile-role-pill' : 'nav-rail-role-pill';

  // Collapsed (56px rail): no fixed max-width/truncate — a clipped "GTME" or
  // "ClayDR" is the bug this fixes. Smaller text + tight padding and the
  // rail's full inner width let the longest label ("ClayDR") fit without an
  // ellipsis; while a lens is active the full copy is still carried in `title`
  // (and reachable via aria-label on the button) for anyone who needs it.
  // `fixed` (mobile) is never collapsed — it always renders the full text.
  const sizeClasses = fixed
    ? 'text-xs px-2.5 py-1.5 shadow shrink-0'
    : collapsed
      ? 'w-full justify-center text-[9px] leading-none tracking-tight px-0.5 py-1 mb-1.5'
      : 'w-full justify-center text-xs px-2.5 py-1 mb-1.5';

  return (
    <div
      data-testid={testidBase}
      title={title}
      className={`flex items-center gap-1 rounded-full border border-nightshade-700 bg-nightshade-800 text-nightshade-200 whitespace-nowrap ${sizeClasses}`}
    >
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label={viewingAs && collapsed ? `User Role: ${label}` : undefined}
        className="outline-none"
      >
        {showFull ? `User Role: ${label}` : label}
      </button>
      {showFull && (
        <button
          type="button"
          aria-label={clearLabel}
          data-testid={`${testidBase}-clear`}
          onClick={onClear}
          className="flex items-center justify-center text-icon-subtle hover:text-nightshade-200 outline-none shrink-0"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

/**
 * Vertical navigation rail with icons that expand to show labels on hover.
 * Sits on the far left of the app layout.
 */
export function NavigationRail({ railConfig }: { railConfig?: MiniAppShellRailConfig }) {
  const isMinimal = railConfig?.variant === 'minimal';
  const hideLogo = railConfig?.hideLogo ?? false;
  const { team, realTeam, viewingAs, isLoaded: roleViewLoaded } = useRoleView();
  const { setTeam } = useSetViewAsRole();
  const theme = useThemeSync();
  const hideTools = railConfig?.hideTools ?? false;
  const hideUserMenu = railConfig?.hideUserMenu ?? false;
  const collapsedWidth = railConfig?.collapsedWidth ?? (isMinimal ? MINIMAL_COLLAPSED_WIDTH : DEFAULT_COLLAPSED_WIDTH);
  const expandedWidth = railConfig?.expandedWidth ?? DEFAULT_EXPANDED_WIDTH;
  const iconClass = isMinimal ? MINIMAL_ICON_CLASS : DEFAULT_ICON_CLASS;
  const sectionBorderClass = isMinimal ? 'border-border-secondary' : 'border-nightshade-700';
  const { isAdmin, canImpersonate, isManager, userType, email } = useUserControlsContext();
  const { active: isImpersonating } = useImpersonation();
  const { signOut } = useClerk();
  const [, setModalParams] = useQueryStates(modalParams);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);

  const showImpersonationSwitcher = canImpersonate;

  const initials = useMemo(
    () => getInitials(email ? email.split('@')[0].replace(/\./g, ' ') : ''),
    [email],
  );

  const displayName = useMemo(() => emailToDisplayName(email), [email]);

  // "User Role: <Team>" row label (R1). A user with no resolvable team
  // (MEMBER/REVOPS, realFlowRole null) gets the bare "User Role". It is never
  // a user-type label ("Member"/"RevOps") standing in for a team they don't
  // have.
  const roleLensLabel = team ? `User Role: ${teamLabel(team)}` : 'User Role';

  // Only expand when hovering the Terra logo; collapse when mouse leaves the entire nav
  const handleLogoMouseEnter = React.useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleNavMouseEnter = React.useCallback(() => {
    if (hideLogo || isMinimal) setIsExpanded(true);
  }, [hideLogo, isMinimal]);

  const handleNavMouseLeave = React.useCallback(() => {
    setIsExpanded(false);
  }, []);

  const hiddenNavKeys = useMemo(() => new Set(railConfig?.hideNavKeys ?? []), [railConfig?.hideNavKeys]);

  // Filter main nav items based on userType and per-app hideNavKeys.
  // Route-based items (isRoute=true) are always visible; view-mode items are filtered by user type
  const filteredMainNavItems = useMemo(() => {
    return MAIN_NAV_ITEMS.filter((item) => {
      if (hiddenNavKeys.has(item.key)) return false;
      if (item.isRoute) return true;
      return canAccessSurface(userType, item.key as AppViewMode);
    });
  }, [userType, hiddenNavKeys]);

  // Determine active key based on current route
  const isMyActionsActive = location.pathname.startsWith('/my-actions');
  const isMyTeamActive = location.pathname.startsWith('/my-team');
  const isLibraryActive = location.pathname.startsWith('/library');
  const isOpenLoopsActive = location.pathname.startsWith('/open-loops');
  const isSignalsActive = location.pathname.startsWith('/signals');
  const isHomeActive = location.pathname === '/' || location.pathname.startsWith('/home');
  const _isAdminRoute = location.pathname.startsWith('/admin');
  const isDocsRoute = location.pathname.startsWith('/docs');

  const getActiveKey = () => {
    if (isHomeActive) return 'home';
    if (isMyActionsActive) return 'my-actions';
    if (isMyTeamActive) return 'my-team';
    if (isLibraryActive) return 'library';
    if (isOpenLoopsActive) return 'open-loops';
    if (isSignalsActive) return 'signals';
    if (location.pathname.startsWith('/flow')) return 'flow';
    if (location.pathname.startsWith('/tools')) return 'tools';
    if (location.pathname.startsWith('/admin')) return 'admin';
    if (isDocsRoute) return 'docs';
    // Derive view from first path segment
    const segment = location.pathname.split('/')[1];
    if (segment && ['accounts', 'pipeline', 'categories'].includes(segment)) return segment;
    return 'home'; // default for root path — `/` redirects to /home
  };
  const activeKey = getActiveKey();

  const handleClick = (item: NavItem) => {
    if (item.external && item.route) {
      window.open(item.route, '_blank', 'noopener,noreferrer');
    } else if (item.isRoute && item.route) {
      navigate(item.route);
    } else if (['categories', 'accounts', 'pipeline'].includes(item.key)) {
      navigate('/' + item.key);
    }
  };

  // V4-only nav items — always visible now that legacy renderer is removed
  const v4Items: NavItem[] = [
    { key: 'my-actions', label: 'My Actions', icon: <History className="w-5 h-5" />, isRoute: true, route: '/my-actions' },
  ].filter((item) => !hiddenNavKeys.has(item.key));

  // Manager-only nav items
  const managerItems: NavItem[] = isManager && !hiddenNavKeys.has('my-team')
    ? [{ key: 'my-team', label: 'My Team', icon: <UsersRound className="w-5 h-5" />, isRoute: true, route: '/my-team/health' }]
    : [];

  // Flow — the unified rep shell (Command Center | Playhouse | Action View).
  // Visible to all users; the /flow shell itself renders no rail.
  const flowItem: NavItem = { key: 'flow', label: 'Flow', icon: <Waves className="w-5 h-5" />, isRoute: true, route: '/flow' };

  // Tools section: visible to all users
  const toolsItem: NavItem = { key: 'tools', label: 'Apps', icon: <Grid3X3 className="w-5 h-5" />, isRoute: true, route: '/tools' };

  const renderNavItem = (item: NavItem) => {
    const isActive = item.key === activeKey;
    const itemIcon = React.isValidElement(item.icon)
      ? React.cloneElement(item.icon as React.ReactElement<{ className?: string; strokeWidth?: number }>, {
          className: iconClass,
          ...(isMinimal ? { strokeWidth: 1.5 } : {}),
        })
      : item.icon;

    const activeClass = isMinimal
      ? 'bg-bg-tertiary text-content-primary'
      : 'bg-nightshade-700 text-white';
    const inactiveClass = isMinimal
      ? 'text-icon-subtle hover:bg-bg-tertiary hover:text-content-primary'
      : 'text-icon-subtle hover:bg-nightshade-800 hover:text-nightshade-200';
    const labelClass = isMinimal ? 'text-sm font-normal' : 'text-sm font-medium';
    const tooltipClass = isMinimal
      ? 'bg-nightshade-800 text-white font-normal'
      : 'bg-nightshade-700 text-white';

    return (
      <div key={item.key} className={`relative group ${isMinimal ? 'w-fit' : ''}`}>
        <button
          onClick={() => handleClick(item)}
          data-testid={`nav-rail-${item.key}`}
          className={`
            flex items-center rounded-lg transition-colors duration-150
            ${isMinimal ? 'w-fit py-1.5' : 'w-full px-3 py-2.5 gap-3'}
            ${isMinimal ? (isExpanded ? 'justify-start gap-2' : 'justify-start gap-0') : ''}
            ${isActive ? activeClass : inactiveClass}
          `}
          style={isMinimal ? { paddingLeft: MINIMAL_ITEM_PX, paddingRight: MINIMAL_ITEM_PX } : undefined}
        >
          <span
            className="flex shrink-0 items-center justify-center"
            style={isMinimal ? { height: MINIMAL_ICON_SLOT, width: MINIMAL_ICON_SLOT } : undefined}
          >
            {itemIcon}
          </span>
          <span
            className={`${labelClass} whitespace-nowrap overflow-hidden ${MINIMAL_EXPAND_MS} ${MINIMAL_EXPAND_EASE} ${
              isMinimal
                ? isExpanded
                  ? 'max-w-[12rem] opacity-100'
                  : 'max-w-0 opacity-0'
                : isExpanded
                  ? 'opacity-100 w-auto'
                  : 'opacity-0 w-0'
            } ${isMinimal ? 'transition-[max-width,opacity]' : 'transition-all duration-200'}`}
          >
            {item.label}
          </span>
        </button>
        {/* Tooltip — only visible when sidebar is collapsed */}
        {!isExpanded && (
          <span className={`pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 ${tooltipClass}`}>
            {item.label}
          </span>
        )}
      </div>
    );
  };

  const navItems = (
    <>
      {filteredMainNavItems.map(renderNavItem)}
      {v4Items.map(renderNavItem)}
      {managerItems.map(renderNavItem)}
      {!hiddenNavKeys.has('flow') && renderNavItem(flowItem)}
    </>
  );

  // Mobile: the hover-driven rail contributes layout width at every one of
  // its 13+ flex-row mount sites, so below the breakpoint we render a
  // fixed-position floating trigger instead — zero layout width, every host
  // gets the full viewport with no per-site changes (Decision 10). It opens
  // the same DropdownMenu composition the desktop user-menu below uses,
  // listing the same item arrays already computed above.
  if (isMobile) {
    const mobileNavItems: NavItem[] = [
      ...filteredMainNavItems,
      ...v4Items,
      ...managerItems,
      ...(hiddenNavKeys.has('flow') ? [] : [flowItem]),
    ];

    const renderMobileNavItem = (item: NavItem) => {
      const isActive = item.key === activeKey;
      const itemIcon = React.isValidElement(item.icon)
        ? React.cloneElement(item.icon as React.ReactElement<{ className?: string }>, {
            className: 'w-4 h-4',
          })
        : item.icon;

      return (
        <DropdownMenu.Item
          key={item.key}
          data-testid={`nav-rail-mobile-${item.key}`}
          className={`flex items-center gap-2.5 px-3 py-2 text-sm outline-none cursor-default transition-colors ${
            isActive
              ? 'bg-nightshade-700 text-white'
              : 'text-nightshade-200 hover:bg-nightshade-800 focus:bg-nightshade-800'
          }`}
          onSelect={() => handleClick(item)}
        >
          <span className="flex shrink-0 items-center justify-center w-4 h-4">{itemIcon}</span>
          {item.label}
        </DropdownMenu.Item>
      );
    };

    return (
      // Mobile header flow (R6): the hamburger trigger and, whenever the rep
      // has an effective team and /api/me has loaded, the team pill sit
      // together in one fixed-position row. Only this wrapper is
      // position-fixed — neither child carries its own fixed offset, so the
      // pill is laid out next to the trigger instead of floating
      // independently at a hard-coded coordinate.
      <div className="fixed top-3 left-3 z-50 flex items-center gap-2">
      <DropdownMenu.Root open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            data-testid="nav-rail-mobile-trigger"
            aria-label="Open navigation menu"
            className="rounded-lg p-2 shadow bg-nightshade-900 text-nightshade-200 shrink-0"
            style={railConfig?.backgroundColor ? { backgroundColor: railConfig.backgroundColor } : undefined}
          >
            <Menu className="w-5 h-5" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side="bottom"
            align="start"
            sideOffset={8}
            className="w-56 rounded-lg border border-nightshade-700 bg-nightshade-900 shadow-popover overflow-hidden z-50"
            data-testid="nav-rail-mobile-dropdown"
          >
            <DropdownMenu.Group>{mobileNavItems.map(renderMobileNavItem)}</DropdownMenu.Group>
            {!hideTools && (
              <>
                <DropdownMenu.Separator className="h-px bg-nightshade-700" />
                <DropdownMenu.Group>{renderMobileNavItem(toolsItem)}</DropdownMenu.Group>
              </>
            )}

            {/* Role view lens, inline on mobile. This group (the shared
                TeamLensItems radios) is shown to everyone. It is an admin
                gate point, see "The admin gate" in docs/role-view-lens.md.
                The gate is `isAdmin && (...)` around it, plus the same gate
                on the desktop Sub. */}
            <DropdownMenu.Separator className="h-px bg-nightshade-700" />
            <DropdownMenu.Group>
              <DropdownMenu.Label
                className="px-3 py-1.5 text-xs font-medium text-icon-subtle"
                data-testid="nav-rail-mobile-team-lens-label"
              >
                User Role
              </DropdownMenu.Label>
              <TeamLensItems
                team={team}
                onPick={setTeam}
                variant="inline"
                itemClassName="flex items-center gap-2.5 px-3 py-2 text-sm outline-none cursor-default transition-colors text-nightshade-200 hover:bg-nightshade-800 focus:bg-nightshade-800"
              />
            </DropdownMenu.Group>

            <DropdownMenu.Separator className="h-px bg-nightshade-700" />
            <DropdownMenu.Group>
              <DropdownMenu.CheckboxItem
                data-testid="nav-rail-mobile-theme-dark"
                className="flex items-center gap-2.5 px-3 py-2 text-sm outline-none cursor-default transition-colors text-nightshade-200 hover:bg-nightshade-800 focus:bg-nightshade-800"
                checked={theme === 'dark'}
                onCheckedChange={(next) => setTheme(next ? 'dark' : 'light')}
                onSelect={(e) => e.preventDefault()}
              >
                <span className="flex shrink-0 items-center justify-center w-4 h-4">
                  <Moon className="w-4 h-4" />
                </span>
                Dark theme
                <DropdownMenu.ItemIndicator className="ml-auto flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.CheckboxItem>
            </DropdownMenu.Group>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {team && roleViewLoaded && (
        <RolePill
          team={team}
          realTeam={realTeam}
          viewingAs={viewingAs}
          collapsed={false}
          fixed
          onOpenMenu={() => setMobileMenuOpen(true)}
          onClear={() => setTeam(null)}
        />
      )}
      </div>
    );
  }

  const minimalNavCollapsedWidth = MINIMAL_RAIL_PL + MINIMAL_PILL_COLLAPSED + MINIMAL_RAIL_PR;
  const minimalExpandedCap = expandedWidth;

  const navStyle: React.CSSProperties = {
    ...(isMinimal
      ? {
          width: 'max-content',
          maxWidth: isExpanded
            ? minimalNavMaxWidth(minimalExpandedCap)
            : minimalNavCollapsedWidth,
        }
      : { width: isExpanded ? expandedWidth : collapsedWidth }),
    ...(railConfig?.backgroundColor ? { backgroundColor: railConfig.backgroundColor } : {}),
  };

  return (
    <nav
      className={`flex-shrink-0 flex flex-col h-full ${
        isMinimal
          ? `pr-1 transition-[max-width] ${MINIMAL_EXPAND_MS} ${MINIMAL_EXPAND_EASE}`
          : 'transition-all duration-200 ease-out'
      } ${railConfig?.backgroundColor ? '' : 'bg-nightshade-900'}`}
      style={{
        ...navStyle,
        ...(isMinimal ? { paddingLeft: MINIMAL_RAIL_PL } : {}),
      }}
      onMouseEnter={isMinimal ? undefined : handleNavMouseEnter}
      onMouseLeave={isMinimal ? undefined : handleNavMouseLeave}
    >
      {/* Logo area — hover to expand sidebar (hidden when manifest.shell.rail.hideLogo) */}
      {!hideLogo && (
        <div
          className={`flex-shrink-0 h-14 flex items-center justify-center border-b ${sectionBorderClass}`}
          onMouseEnter={handleLogoMouseEnter}
        >
          <Link to="/" className="flex items-center justify-center">
            <img
              src="/claybot_logo.png"
              alt="Terra"
              className="h-8 w-8 rounded-lg object-cover flex-shrink-0"
            />
            <span
              className={`text-white font-semibold text-sm whitespace-nowrap overflow-hidden transition-all duration-200 ${
                isExpanded ? 'opacity-100 w-auto ml-3' : 'opacity-0 w-0 ml-0'
              }`}
            >
              Terra
            </span>
          </Link>
        </div>
      )}

      {/* Main navigation items - filtered by userType */}
      {isMinimal ? (
        <div className="flex flex-1 items-center justify-start">
          <div
            className={`rounded-xl border border-border-secondary flex flex-col items-start gap-0.5 overflow-hidden transition-[max-width] ${MINIMAL_EXPAND_MS} ${MINIMAL_EXPAND_EASE}`}
            style={{
              width: 'max-content',
              maxWidth: isExpanded
                ? minimalPillMaxWidth(minimalExpandedCap)
                : MINIMAL_PILL_COLLAPSED,
              padding: MINIMAL_PILL_INSET,
            }}
            onMouseEnter={handleNavMouseEnter}
            onMouseLeave={handleNavMouseLeave}
          >
            {navItems}
          </div>
        </div>
      ) : (
        <div className={`flex-1 space-y-1 ${hideLogo ? 'py-3 px-1.5' : 'py-3 px-2'}`}>
          {navItems}
        </div>
      )}

      {/* Tools section */}
      {toolsItem && !hideTools && (
        <div className={`space-y-1 border-t ${sectionBorderClass} ${hideLogo ? 'py-3 px-1.5' : 'py-3 px-2'} ${isMinimal ? 'flex flex-col items-center w-full' : ''}`}>
          {renderNavItem(toolsItem)}
        </div>
      )}

      {/* User avatar + menu at bottom */}
      {!hideUserMenu && (
      <div className={`border-t ${sectionBorderClass} ${hideLogo ? 'px-1.5 py-3' : 'px-2 py-3'} ${isMinimal ? 'flex flex-col items-center w-full' : ''}`}>
        {/* Team pill (R6): above the avatar menu whenever the rep has an
            effective team and /api/me has loaded. No lens: the team label
            only. Lens active: collapsed rail shows the label only; expanded
            shows "User Role: <Team>" + x. */}
        {team && roleViewLoaded && (
          <RolePill
            team={team}
            realTeam={realTeam}
            viewingAs={viewingAs}
            collapsed={!isExpanded}
            onOpenMenu={() => setDropdownOpen(true)}
            onClear={() => setTeam(null)}
          />
        )}
        <DropdownMenu.Root
          open={dropdownOpen}
          onOpenChange={setDropdownOpen}
        >
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              data-testid="nav-rail-user-menu"
              className={`w-full flex items-center justify-center rounded-lg transition-all duration-150 ${
                isMinimal
                  ? 'px-2 py-2 hover:bg-bg-tertiary data-[state=open]:bg-bg-tertiary'
                  : 'px-3 py-2.5 hover:bg-nightshade-800 data-[state=open]:bg-nightshade-700'
              }`}
            >
              <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold flex-shrink-0 ${
                isMinimal
                  ? 'bg-bg-tertiary text-content-tertiary'
                  : 'h-8 w-8 bg-nightshade-600 text-nightshade-100'
              } ${isImpersonating ? `ring-2 ring-tangerine-500 ring-offset-1 ${isMinimal ? 'ring-offset-nightshade-50' : 'ring-offset-nightshade-900'}` : ''}`}>
                {initials}
              </span>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              side="top"
              align="start"
              sideOffset={8}
              className="rounded-lg border border-nightshade-700 bg-nightshade-900 shadow-popover overflow-hidden z-50"
              data-testid="nav-rail-user-dropdown"
            >
              <div className="w-56">
                  {/* User info header */}
                  <DropdownMenu.Label className="px-3 py-3 border-b border-nightshade-700">
                    <div className="text-sm font-medium text-nightshade-100 truncate">
                      {displayName}
                    </div>
                    <div className="text-xs text-icon-subtle truncate mt-0.5">{email}</div>
                  </DropdownMenu.Label>

                  <DropdownMenu.Group>
                    {showImpersonationSwitcher && (
                      <DropdownMenu.Item
                        className="flex items-center gap-2.5 px-3 py-2 text-sm text-nightshade-200 outline-none cursor-default hover:bg-nightshade-800 focus:bg-nightshade-800 transition-colors"
                        onSelect={(e) => {
                          e.preventDefault();
                          setDropdownOpen(false);
                          openImpersonationPalette();
                        }}
                        data-testid="nav-rail-switch-user"
                      >
                        <ArrowLeftRight className="w-4 h-4 text-icon-subtle" />
                        Switch user...
                      </DropdownMenu.Item>
                    )}

                    {/* Role view lens. This Sub (the shared TeamLensItems
                        radios) is shown to everyone. It is an admin gate
                        point, see "The admin gate" in docs/role-view-lens.md.
                        The gate is `isAdmin && (...)` around it, plus the
                        same gate on the mobile group. */}
                    <DropdownMenu.Sub>
                      <DropdownMenu.SubTrigger
                        className="flex items-center gap-2.5 px-3 py-2 text-sm text-nightshade-200 outline-none cursor-default hover:bg-nightshade-800 focus:bg-nightshade-800 transition-colors"
                        data-testid="nav-rail-team-lens"
                      >
                        <Eye className="w-4 h-4 text-icon-subtle" />
                        {roleLensLabel}
                      </DropdownMenu.SubTrigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.SubContent
                          className="rounded-lg border border-nightshade-700 bg-nightshade-900 shadow-popover overflow-hidden z-50 w-48"
                          data-testid="nav-rail-team-lens-content"
                        >
                          <TeamLensItems
                            team={team}
                            onPick={setTeam}
                            variant="submenu"
                            itemClassName="flex items-center gap-2.5 px-3 py-2 text-sm text-nightshade-200 outline-none cursor-default hover:bg-nightshade-800 focus:bg-nightshade-800 transition-colors"
                          />
                        </DropdownMenu.SubContent>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Sub>

                    {(isAdmin || canImpersonate) && (
                      <DropdownMenu.Item
                        className="flex items-center gap-2.5 px-3 py-2 text-sm text-nightshade-200 outline-none cursor-default hover:bg-nightshade-800 focus:bg-nightshade-800 transition-colors"
                        onSelect={() => navigate('/admin')}
                        data-testid="nav-rail-admin"
                      >
                        <Shield className="w-4 h-4 text-icon-subtle" />
                        Admin
                      </DropdownMenu.Item>
                    )}

                    <DropdownMenu.Item
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-nightshade-200 outline-none cursor-default hover:bg-nightshade-800 focus:bg-nightshade-800 transition-colors"
                      onSelect={() => { void setModalParams({ modal: 'preferences' }); }}
                      data-testid="nav-rail-preferences"
                    >
                      <Settings className="w-4 h-4 text-icon-subtle" />
                      Preferences
                    </DropdownMenu.Item>
                  </DropdownMenu.Group>

                  <DropdownMenu.Separator className="h-px bg-nightshade-700" />

                  <DropdownMenu.Group>
                    <DropdownMenu.CheckboxItem
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-nightshade-200 outline-none cursor-default hover:bg-nightshade-800 focus:bg-nightshade-800 transition-colors"
                      checked={theme === 'dark'}
                      onCheckedChange={(next) => setTheme(next ? 'dark' : 'light')}
                      onSelect={(e) => e.preventDefault()}
                      data-testid="nav-rail-theme-dark"
                    >
                      <Moon className="w-4 h-4 text-icon-subtle" />
                      Dark theme
                      <DropdownMenu.ItemIndicator className="ml-auto flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </DropdownMenu.ItemIndicator>
                    </DropdownMenu.CheckboxItem>
                  </DropdownMenu.Group>

                  <DropdownMenu.Separator className="h-px bg-nightshade-700" />

                  <DropdownMenu.Group>
                    <DropdownMenu.Item
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-nightshade-200 outline-none cursor-default hover:bg-nightshade-800 focus:bg-nightshade-800 transition-colors"
                      onSelect={() => { void signOut(); }}
                      data-testid="nav-rail-sign-out"
                    >
                      <LogOut className="w-4 h-4 text-icon-subtle" />
                      Log out
                    </DropdownMenu.Item>
                  </DropdownMenu.Group>
                </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
      )}
    </nav>
  );
}
